import React, { useMemo, useState } from "react";
import { Section, Eyebrow, Lede } from "../components/site/Section";
import SubscribeForm from "../components/site/SubscribeForm";
import script from "../lib/pilot_script.json";
import { BookOpen } from "lucide-react";

const FREE_SCENES = 3; // show first N scenes; rest gated behind subscribe

function ScreenplayLine({ element }) {
  const { type, text } = element;
  if (type === "action" || type === "general") {
    return <p className="font-mono text-[15px] text-[#EAE0D5]/90 leading-7 mb-3 whitespace-pre-wrap">{text}</p>;
  }
  if (type === "character") {
    return (
      <p className="font-mono text-[14px] tracking-[0.18em] uppercase text-[#C5A059] mt-5 mb-1 pl-16 md:pl-32">
        {text}
      </p>
    );
  }
  if (type === "parenthetical") {
    return (
      <p className="font-mono text-[13px] italic text-[#A39E99] pl-12 md:pl-28 mb-1">
        {text.startsWith("(") ? text : `(${text})`}
      </p>
    );
  }
  if (type === "dialogue") {
    return (
      <p className="font-mono text-[15px] text-[#EAE0D5] leading-7 pl-8 md:pl-20 pr-8 md:pr-20 mb-4">
        {text}
      </p>
    );
  }
  if (type === "transition") {
    return (
      <p className="font-mono text-[13px] tracking-[0.22em] uppercase text-[#C5A059]/80 text-right mt-4 mb-4">
        {text}
      </p>
    );
  }
  if (type === "shot") {
    return (
      <p className="font-mono text-[14px] tracking-[0.15em] uppercase text-[#EAE0D5]/80 mt-4 mb-2">
        {text}
      </p>
    );
  }
  // fallback
  return <p className="font-mono text-[14px] text-[#A39E99] mb-2">{text}</p>;
}

function Scene({ scene, index }) {
  return (
    <article
      data-testid={`pilot-scene-${index + 1}`}
      className="mb-10 md:mb-14 pb-10 md:pb-14 border-b border-white/5 last:border-b-0"
    >
      <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]/80 mb-3">
        Scene {index + 1}
      </div>
      <h3 className="font-mono text-[15px] md:text-base tracking-wider uppercase text-[#EAE0D5] mb-6">
        {scene.heading || "—"}
      </h3>
      <div>
        {scene.elements.map((el, i) => (
          <ScreenplayLine key={i} element={el} />
        ))}
      </div>
    </article>
  );
}

export default function PilotPage() {
  const scenes = script.scenes || [];
  const [unlocked, setUnlocked] = useState(false);

  const visibleScenes = useMemo(() => {
    if (unlocked) return scenes;
    return scenes.slice(0, FREE_SCENES);
  }, [scenes, unlocked]);

  const hiddenCount = scenes.length - FREE_SCENES;

  return (
    <div data-testid="pilot-page">
      <Section testId="pilot-hero" className="pt-12 md:pt-24">
        <div className="ornate-rule text-xs tracking-[0.4em] uppercase">
          <span>Episode 01 · The Mistake</span>
        </div>
        <h1
          data-testid="pilot-title"
          className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-6 max-w-4xl"
        >
          Read the{" "}
          <span className="text-[#C5A059] italic font-light">Pilot</span>
        </h1>
        <Lede testId="pilot-lede">
          The cold open of Chains Beneath Bakhtak. A sealed desert transport
          cuts through the night toward a forest that has never known intrusion.
          Three strangers are about to collide.
        </Lede>
        <p className="text-xs text-[#737373] mt-6 tracking-[0.2em] uppercase">
          {scenes.length} scenes · screenplay by Isaiah M. Willis-Dean
        </p>
      </Section>

      {/* READER */}
      <Section testId="pilot-reader" className="pt-0">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12">
          <aside className="md:col-span-3 order-2 md:order-1">
            <div className="md:sticky md:top-24 bg-[#0A0A0A] border border-white/5 p-5">
              <div className="flex items-center gap-2 text-[#C5A059]">
                <BookOpen size={16} strokeWidth={1.5} />
                <span className="text-[10px] tracking-[0.28em] uppercase">
                  Scene Index
                </span>
              </div>
              <ol
                className="mt-4 space-y-2 text-xs text-[#A39E99] list-decimal list-inside max-h-[60vh] overflow-y-auto pr-1"
                data-testid="pilot-scene-index"
              >
                {scenes.map((s, i) => (
                  <li
                    key={i}
                    className={`leading-snug truncate ${i < FREE_SCENES || unlocked ? "text-[#A39E99]" : "text-[#737373]"}`}
                    title={s.heading}
                  >
                    {s.title || s.heading || `Scene ${i + 1}`}
                  </li>
                ))}
              </ol>
              <div className="mt-5 text-[10px] text-[#737373] tracking-wider uppercase">
                {unlocked
                  ? `Unlocked · ${scenes.length} scenes`
                  : `Preview · ${FREE_SCENES}/${scenes.length} scenes`}
              </div>
            </div>
          </aside>

          <article
            className="md:col-span-9 order-1 md:order-2"
            data-testid="pilot-pages"
          >
            <div className="bg-[#0A0A0A] border border-white/5 p-6 md:p-12">
              {visibleScenes.map((s, i) => (
                <Scene key={i} scene={s} index={i} />
              ))}

              {!unlocked && hiddenCount > 0 && (
                <div
                  className="relative -mx-6 md:-mx-12 -mb-6 md:-mb-12 mt-8 px-6 md:px-12 py-10 md:py-16 border-t border-[#C5A059]/20 bg-gradient-to-b from-transparent via-[#0A0A0A] to-[#050505] text-center"
                  data-testid="pilot-lock-wall"
                >
                  <div className="ornate-rule justify-center text-[#C5A059] text-xs tracking-[0.32em] uppercase">
                    <span>{hiddenCount} more scenes locked</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-5">
                    Continue the pilot.
                  </h3>
                  <p className="text-[#A39E99] mt-3 max-w-md mx-auto text-sm md:text-base">
                    Subscribe to read the full screenplay — the desert ambush,
                    Shion's awakening, the Verdani descent. No spam, no charge.
                  </p>
                  <div className="mt-7 max-w-md mx-auto">
                    <SubscribeForm
                      source="pilot"
                      testIdPrefix="pilot-subscribe"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setUnlocked(true)}
                    data-testid="pilot-unlock-btn"
                    className="mt-6 text-xs tracking-[0.22em] uppercase text-[#C5A059]/70 hover:text-[#C5A059] underline-offset-4 hover:underline"
                  >
                    Or read the full pilot now
                  </button>
                </div>
              )}

              {unlocked && (
                <div className="text-center pt-6 mt-6 border-t border-white/5">
                  <p className="text-xs tracking-[0.22em] uppercase text-[#C5A059]/80">
                    End of Episode 01 · "The Mistake"
                  </p>
                  <p className="text-[#737373] mt-2 text-sm">
                    Six episodes complete Season One.
                  </p>
                </div>
              )}
            </div>
          </article>
        </div>
      </Section>
    </div>
  );
}
